import { item } from "../data/items.ts";
import type { ArmorItem, BirdLook, BootItem, Gear, HelmetItem, HumanLook, Look, ShieldItem, WeaponItem } from "../types.ts";
import { drawArmor, drawBoot, drawHelmet, drawShield, drawWeapon } from "./gear.ts";
import { ellipse, hash, INK, mulberry, paint, shade, type Ctx } from "./util.ts";

export interface GearView {
  weapon?: WeaponItem;
  shield?: ShieldItem;
  helmet?: HelmetItem;
  armor?: ArmorItem;
  boots?: BootItem;
}

export function gearView(gear: Gear): GearView {
  const v: GearView = {};
  for (const id of Object.values(gear)) {
    if (!id) continue;
    const it = item(id);
    switch (it.slot) {
      case "weapon": v.weapon = it; break;
      case "shield": v.shield = it; break;
      case "helmet": v.helmet = it; break;
      case "armor": v.armor = it; break;
      case "boots": v.boots = it; break;
    }
  }
  return v;
}

export interface Pose {
  /** Weapon arm/wing angle in radians; 0.6 is the resting guard. */
  wing: number;
  /** Shield raise, 0..1. */
  guard: number;
  /** Body lean in radians; positive leans forward. */
  lean: number;
  walk: number;
  stride: number;
  squash: number;
  mouth: number;
  sit: number;
  flap: number;
  eyes: "open" | "closed" | "x";
}

export const REST_POSE: Pose = { wing: 0.6, guard: 0, lean: 0, walk: 0, stride: 0, squash: 0, mouth: 0, sit: 0, flap: 0, eyes: "open" };

/** Draws a fighter facing +x with feet at the origin. */
export function drawFighter(ctx: Ctx, look: Look, gear: GearView, pose: Pose, time: number): void {
  if (look.kind === "bird") drawBird(ctx, look, gear, pose, time);
  else drawHuman(ctx, look, gear, pose, time);
}

/** Approximate figure height, for placing HUD elements and effects above the head. */
export function figureHeight(look: Look): number {
  if (look.kind === "human") return 205 * look.size;
  return (64 + 30 * look.legLen + 36 * (look.neck - 1) + (look.shape === "penguin" ? 20 : 0)) * look.size;
}

function weaponOffset(w: WeaponItem | undefined): number {
  return w && w.reach === 2 ? -1.15 : -1.85;
}

// ---------------------------------------------------------------- birds

function drawBird(ctx: Ctx, L: BirdLook, g: GearView, p: Pose, time: number): void {
  const pen = L.shape === "penguin";
  const legH = 30 * L.legLen * (1 - p.sit * 0.55);
  const rx = pen ? 25 : 36;
  const ry = (pen ? 38 : 27) * (1 - p.squash * 0.12);
  const cx = 0;
  const cy = -legH - ry + 6;
  const headColor = L.head ?? L.body;
  const hr = 15;
  const hx = pen ? cx + 7 : cx + 24 + (L.neck - 1) * 10;
  const hy = pen ? cy - ry - 2 : cy - 33 - (L.neck - 1) * 34;

  // legs (unleaned, behind body)
  const hipY = cy + ry * 0.55;
  for (const near of [false, true]) {
    const phase = p.walk + (near ? Math.PI : 0);
    const fx = (near ? 7 : -5) + Math.sin(phase) * 11 * p.stride;
    const lift = Math.max(0, Math.cos(phase)) * 7 * p.stride;
    const hipX = near ? 6 : -6;
    drawBirdLeg(ctx, L, g.boots, hipX, hipY, fx, -lift, near, time);
  }

  ctx.save();
  ctx.translate(0, -legH);
  ctx.rotate(p.lean);
  ctx.translate(0, legH);

  // turkey fan / royal cape go behind everything
  if (L.tailStyle === "fan") drawFan(ctx, L, cx - 10, cy - 6, time);
  if (g.armor?.shape === "royal") {
    ctx.beginPath();
    ctx.moveTo(cx - 4, cy - ry * 0.7);
    ctx.quadraticCurveTo(cx - rx - 22, cy, cx - rx - 14 + Math.sin(time * 3) * 3, cy + ry + 8);
    ctx.lineTo(cx + 6, cy + ry * 0.6);
    ctx.closePath();
    paint(ctx, "#a3162f");
  }
  if (L.tailStyle !== "fan") drawTail(ctx, L, cx - rx * 0.82, cy - ry * 0.3);

  // far wing
  ctx.save();
  ctx.translate(cx + 10, cy - 6);
  ctx.rotate(1.0 - p.guard * 0.9 - p.flap * 1.2);
  drawWing(ctx, shade(L.wing, -0.18), pen);
  ctx.restore();

  // neck + head outline, then fills (so they merge)
  if (!pen) {
    ctx.lineCap = "round";
    ctx.strokeStyle = INK;
    ctx.lineWidth = 26;
    ctx.beginPath();
    ctx.moveTo(cx + 14, cy - 10);
    ctx.lineTo(hx - 2, hy + 4);
    ctx.stroke();
  }
  ellipse(ctx, hx, hy, hr + 1.5, hr + 1.5);
  ctx.fillStyle = INK;
  ctx.fill();
  if (!pen) {
    ctx.strokeStyle = headColor;
    ctx.lineWidth = 20;
    ctx.beginPath();
    ctx.moveTo(cx + 14, cy - 10);
    ctx.lineTo(hx - 2, hy + 4);
    ctx.stroke();
    ctx.lineCap = "butt";
  }

  // body
  ellipse(ctx, cx, cy, rx, ry);
  ctx.fillStyle = L.body;
  ctx.fill();
  ctx.save();
  ellipse(ctx, cx, cy, rx, ry);
  ctx.clip();
  if (L.belly) {
    ellipse(ctx, cx + (pen ? 8 : rx * 0.35), cy + (pen ? 4 : ry * 0.35), pen ? 17 : rx * 0.62, pen ? 32 : ry * 0.62);
    ctx.fillStyle = L.belly;
    ctx.fill();
  }
  drawPattern(ctx, L, cx, cy, rx, ry);
  ellipse(ctx, cx - rx * 0.1, cy + ry * 0.9, rx * 1.1, ry * 0.5);
  ctx.fillStyle = "rgba(0,0,0,0.12)";
  ctx.fill();
  if (g.armor) drawArmor(ctx, g.armor, cx, cy, rx, ry);
  ctx.restore();
  ellipse(ctx, cx, cy, rx, ry);
  ctx.lineWidth = 3;
  ctx.strokeStyle = INK;
  ctx.stroke();

  // head
  if (L.tailStyle === "fan" || L.combStyle === "snood") drawWattle(ctx, L, hx, hy, hr, true);
  ellipse(ctx, hx, hy, hr, hr);
  ctx.fillStyle = headColor;
  ctx.fill();
  if (!g.helmet || g.helmet.shape === "crown" || g.helmet.shape === "eggshell") drawComb(ctx, L, hx, hy, hr, time);
  drawBeak(ctx, L, hx, hy, hr, p.mouth);
  if (L.wattle && L.combStyle !== "snood") drawWattle(ctx, L, hx, hy, hr, false);
  drawEye(ctx, L, hx, hy, hr, p.eyes, !!g.helmet && g.helmet.shape === "knight");
  drawExtra(ctx, L, hx, hy, hr, cx, cy, time);
  if (g.helmet) {
    ctx.save();
    ctx.translate(hx, hy);
    drawHelmet(ctx, g.helmet, hr, time);
    ctx.restore();
  }

  // shield in front of the chest
  if (g.shield) {
    ctx.save();
    ctx.translate(cx + rx * 0.72 + p.guard * 12, cy + 4 - p.guard * 14);
    ctx.rotate(-p.guard * 0.15);
    drawShield(ctx, g.shield, time);
    ctx.restore();
  }

  // near wing + weapon
  ctx.save();
  ctx.translate(cx + 2, cy - 4);
  ctx.rotate(p.wing - (g.shield ? 0 : p.flap * 1.4));
  if (g.weapon) {
    ctx.save();
    ctx.translate(30, 0);
    ctx.rotate(weaponOffset(g.weapon));
    drawWeapon(ctx, g.weapon, time);
    ctx.restore();
  }
  drawWing(ctx, L.wing, pen);
  ctx.restore();

  ctx.restore();
}

function drawBirdLeg(ctx: Ctx, L: BirdLook, boots: BootItem | undefined, hx: number, hy: number, fx: number, fy: number, near: boolean, time: number): void {
  const color = near ? L.legs : shade(L.legs, -0.15);
  const ax = fx;
  const ay = fy - 5;
  ctx.lineCap = "round";
  ctx.strokeStyle = INK;
  ctx.lineWidth = 8;
  ctx.beginPath();
  ctx.moveTo(hx, hy);
  ctx.lineTo(ax, ay);
  ctx.moveTo(ax, ay);
  ctx.lineTo(ax + 12, fy);
  ctx.moveTo(ax, ay);
  ctx.lineTo(ax + 7, fy + 1);
  ctx.moveTo(ax, ay);
  ctx.lineTo(ax - 7, fy);
  ctx.stroke();
  ctx.strokeStyle = color;
  ctx.lineWidth = 4;
  ctx.stroke();
  ctx.lineCap = "butt";
  if (boots) drawBoot(ctx, boots, hx, hy, ax, ay, time);
}

function drawWing(ctx: Ctx, color: string, flipper: boolean): void {
  if (flipper) {
    ellipse(ctx, 16, 0, 20, 7);
    paint(ctx, color);
    return;
  }
  ctx.beginPath();
  ctx.moveTo(-2, -9);
  ctx.quadraticCurveTo(22, -14, 36, -3);
  ctx.lineTo(31, 0);
  ctx.lineTo(36, 4);
  ctx.lineTo(28, 6);
  ctx.lineTo(30, 10);
  ctx.quadraticCurveTo(10, 14, -2, 8);
  ctx.closePath();
  paint(ctx, color);
  ctx.strokeStyle = shade(color, -0.3);
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(6, -2);
  ctx.quadraticCurveTo(16, 0, 26, 1);
  ctx.moveTo(4, 4);
  ctx.quadraticCurveTo(14, 7, 24, 6);
  ctx.stroke();
}

function drawTail(ctx: Ctx, L: BirdLook, tx: number, ty: number): void {
  switch (L.tailStyle) {
    case "fluffy":
      for (const [a, len] of [[-2.55, 24], [-2.2, 30], [-1.85, 26]] as const) {
        ctx.save();
        ctx.translate(tx + 6, ty + 4);
        ctx.rotate(a);
        ellipse(ctx, len * 0.6, 0, len * 0.65, 8);
        paint(ctx, L.tail);
        ctx.restore();
      }
      break;
    case "sickle":
      ctx.lineCap = "round";
      for (const [dx, dy, w] of [[-44, 8, 0], [-52, -4, 1], [-40, 20, 2]] as const) {
        ctx.beginPath();
        ctx.moveTo(tx + 6, ty + 6);
        ctx.bezierCurveTo(tx - 6, ty - 46 + w * 10, tx + dx - 6, ty - 46 + w * 6, tx + dx, ty + dy);
        ctx.strokeStyle = INK;
        ctx.lineWidth = 12 - w * 2;
        ctx.stroke();
        ctx.strokeStyle = w === 1 ? shade(L.tail, 0.2) : L.tail;
        ctx.lineWidth = 7 - w * 2;
        ctx.stroke();
      }
      ctx.lineCap = "butt";
      break;
    case "stub":
      ctx.beginPath();
      ctx.moveTo(tx + 8, ty - 4);
      ctx.lineTo(tx - 14, ty - 12);
      ctx.lineTo(tx - 10, ty + 2);
      ctx.lineTo(tx + 8, ty + 10);
      ctx.closePath();
      paint(ctx, L.tail);
      break;
    case "fan":
      break;
  }
}

function drawFan(ctx: Ctx, L: BirdLook, x: number, y: number, time: number): void {
  const n = 11;
  const r = 66 + Math.sin(time * 2) * 1.5;
  for (let i = 0; i < n; i++) {
    const a0 = Math.PI * 1.0 + (i / n) * Math.PI * 0.95;
    const a1 = a0 + (Math.PI * 0.95) / n;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.arc(x, y, r, a0, a1);
    ctx.closePath();
    paint(ctx, i % 2 ? L.tail : shade(L.tail, -0.15), 2);
  }
  ctx.beginPath();
  ctx.arc(x, y, r - 8, Math.PI, Math.PI * 1.95);
  ctx.strokeStyle = shade(L.tail, 0.5);
  ctx.lineWidth = 5;
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(x, y, r - 2, Math.PI, Math.PI * 1.95);
  ctx.strokeStyle = shade(L.tail, -0.4);
  ctx.lineWidth = 3;
  ctx.stroke();
}

function drawPattern(ctx: Ctx, L: BirdLook, cx: number, cy: number, rx: number, ry: number): void {
  if (L.pattern === "solid") return;
  const col = L.patternColor ?? shade(L.body, -0.45);
  const r = mulberry(hash(L.body + L.wing + L.pattern));
  ctx.fillStyle = col;
  ctx.strokeStyle = col;
  if (L.pattern === "speckled") {
    for (let i = 0; i < 46; i++) {
      const a = r() * Math.PI * 2;
      const d = Math.sqrt(r());
      ellipse(ctx, cx + Math.cos(a) * rx * d, cy + Math.sin(a) * ry * d, 1.6 + r() * 1.6, 1.4 + r() * 1.2, r() * 3);
      ctx.fill();
    }
  } else if (L.pattern === "barred") {
    ctx.lineWidth = 3;
    for (let y = cy - ry; y < cy + ry; y += 7) {
      ctx.beginPath();
      for (let x = cx - rx; x <= cx + rx; x += 6) ctx.lineTo(x, y + Math.sin(x * 0.35) * 2);
      ctx.stroke();
    }
  } else {
    ctx.lineWidth = 2;
    for (let row = 0, y = cy - ry; y < cy + ry + 6; y += 8, row++) {
      for (let x = cx - rx + (row % 2) * 6; x < cx + rx + 6; x += 12) {
        ctx.beginPath();
        ctx.arc(x, y, 6, 0.1, Math.PI - 0.1);
        ctx.stroke();
      }
    }
  }
}

function drawComb(ctx: Ctx, L: BirdLook, hx: number, hy: number, hr: number, time: number): void {
  const c = L.comb;
  switch (L.combStyle) {
    case "single": {
      ctx.beginPath();
      ctx.moveTo(hx - hr * 0.7, hy - hr * 0.55);
      const bumps = 5;
      for (let i = 0; i < bumps; i++) {
        const t = i / (bumps - 1);
        const x = hx - hr * 0.6 + t * hr * 1.15;
        const h = 9 + Math.sin(t * Math.PI) * 5;
        ctx.quadraticCurveTo(x - 3, hy - hr - h - 2, x + 2, hy - hr - h + 4);
      }
      ctx.lineTo(hx + hr * 0.6, hy - hr * 0.6);
      ctx.closePath();
      paint(ctx, c);
      break;
    }
    case "rose":
      ellipse(ctx, hx - 1, hy - hr + 1, hr * 0.7, 6);
      paint(ctx, c);
      for (let i = 0; i < 4; i++) {
        ellipse(ctx, hx - hr * 0.5 + i * 5, hy - hr - 2, 1.4, 1.4);
        ctx.fillStyle = shade(c, 0.35);
        ctx.fill();
      }
      break;
    case "crest":
      for (let i = 0; i < 9; i++) {
        const a = -Math.PI * 0.95 + (i / 8) * Math.PI * 0.8 + Math.sin(time * 3 + i) * 0.04;
        ctx.save();
        ctx.translate(hx - 2, hy - hr * 0.5);
        ctx.rotate(a);
        ellipse(ctx, 13, 0, 13, 5);
        paint(ctx, i % 2 ? L.wing : L.tail, 2);
        ctx.restore();
      }
      break;
    case "mohawk":
      ctx.beginPath();
      ctx.moveTo(hx - hr * 0.9, hy - hr * 0.3);
      for (let i = 0; i < 5; i++) {
        const x = hx - hr * 0.75 + i * hr * 0.38;
        ctx.lineTo(x - 2 - i * 1.5, hy - hr - 18 - (i === 2 ? 4 : 0));
        ctx.lineTo(x + hr * 0.2, hy - hr * 0.8);
      }
      ctx.lineTo(hx + hr * 0.7, hy - hr * 0.5);
      ctx.closePath();
      paint(ctx, c);
      break;
    case "snood":
      ctx.beginPath();
      ctx.moveTo(hx + hr * 0.4, hy - hr * 0.6);
      ctx.quadraticCurveTo(hx + hr + 10, hy - hr * 0.9, hx + hr + 8, hy + hr * 0.9);
      ctx.quadraticCurveTo(hx + hr + 2, hy + hr * 1.1, hx + hr + 3, hy - hr * 0.1);
      ctx.closePath();
      paint(ctx, c, 2.5);
      break;
    case "none":
      break;
  }
}

function drawBeak(ctx: Ctx, L: BirdLook, hx: number, hy: number, hr: number, mouth: number): void {
  const b = L.beak;
  const open = mouth * 7;
  const x0 = hx + hr * 0.72;
  ctx.lineJoin = "round";
  switch (L.beakStyle) {
    case "pointy":
    case "short": {
      const len = L.beakStyle === "pointy" ? 15 : 9;
      ctx.beginPath();
      ctx.moveTo(x0, hy + 2);
      ctx.lineTo(x0 + len * 0.8, hy + 3 + open * 0.8);
      ctx.lineTo(x0, hy + 6 + open * 0.3);
      ctx.closePath();
      paint(ctx, shade(b, -0.15), 2.5);
      ctx.beginPath();
      ctx.moveTo(x0 - 1, hy - 5);
      ctx.quadraticCurveTo(x0 + len * 0.6, hy - 4, x0 + len, hy + 1 - open * 0.2);
      ctx.lineTo(x0, hy + 3);
      ctx.closePath();
      paint(ctx, b, 2.5);
      break;
    }
    case "hooked": {
      ctx.beginPath();
      ctx.moveTo(x0, hy + 3);
      ctx.lineTo(x0 + 11, hy + 4 + open);
      ctx.lineTo(x0, hy + 7 + open * 0.3);
      ctx.closePath();
      paint(ctx, shade(b, -0.15), 2.5);
      ctx.beginPath();
      ctx.moveTo(x0 - 2, hy - 6);
      ctx.quadraticCurveTo(x0 + 12, hy - 8, x0 + 17, hy + 1 - open * 0.2);
      ctx.quadraticCurveTo(x0 + 17, hy + 8, x0 + 12, hy + 7);
      ctx.quadraticCurveTo(x0 + 12, hy + 3, x0, hy + 3);
      ctx.closePath();
      paint(ctx, b, 2.5);
      break;
    }
    case "duck": {
      ctx.beginPath();
      ctx.moveTo(x0 - 2, hy + 3);
      ctx.lineTo(x0 + 18, hy + 3 + open);
      ctx.quadraticCurveTo(x0 + 24, hy + 5 + open, x0 + 18, hy + 7 + open);
      ctx.lineTo(x0 - 2, hy + 7);
      ctx.closePath();
      paint(ctx, shade(b, -0.15), 2.5);
      ctx.beginPath();
      ctx.moveTo(x0 - 3, hy - 5);
      ctx.quadraticCurveTo(x0 + 10, hy - 4, x0 + 20, hy - 1 - open * 0.2);
      ctx.quadraticCurveTo(x0 + 27, hy + 1, x0 + 20, hy + 4);
      ctx.lineTo(x0 - 2, hy + 4);
      ctx.closePath();
      paint(ctx, b, 2.5);
      break;
    }
  }
  ctx.lineJoin = "miter";
}

function drawWattle(ctx: Ctx, L: BirdLook, hx: number, hy: number, hr: number, big: boolean): void {
  ctx.beginPath();
  const x = hx + hr * 0.55;
  const y = hy + hr * 0.45;
  if (big) {
    ctx.moveTo(x - 10, y - 4);
    ctx.bezierCurveTo(x - 14, y + 26, x + 8, y + 24, x + 4, y - 2);
  } else {
    ctx.moveTo(x - 3, y);
    ctx.bezierCurveTo(x - 7, y + 16, x + 7, y + 16, x + 4, y);
  }
  ctx.closePath();
  paint(ctx, L.comb, 2.5);
}

function drawEye(ctx: Ctx, L: BirdLook, hx: number, hy: number, hr: number, state: Pose["eyes"], visored: boolean): void {
  if (visored) return;
  const ex = hx + hr * 0.3;
  const ey = hy - hr * 0.22;
  ctx.strokeStyle = INK;
  ctx.lineWidth = 2.5;
  if (state === "x") {
    ctx.beginPath();
    ctx.moveTo(ex - 4, ey - 4);
    ctx.lineTo(ex + 4, ey + 4);
    ctx.moveTo(ex + 4, ey - 4);
    ctx.lineTo(ex - 4, ey + 4);
    ctx.stroke();
    return;
  }
  if (L.eyes === "shades") {
    ctx.beginPath();
    ctx.roundRect(hx - hr * 0.35, ey - 5, hr * 1.2, 9, 3);
    paint(ctx, "#111", 2);
    ctx.fillStyle = "rgba(255,255,255,0.5)";
    ctx.fillRect(ex + 1, ey - 3, 4, 2);
    ctx.beginPath();
    ctx.moveTo(hx - hr * 0.35, ey - 2);
    ctx.lineTo(hx - hr, ey - 4);
    ctx.stroke();
    return;
  }
  if (state === "closed") {
    ctx.beginPath();
    ctx.arc(ex, ey, 4, 0.2, Math.PI - 0.2);
    ctx.stroke();
    return;
  }
  ellipse(ctx, ex, ey, 5, 5);
  paint(ctx, "#fff", 2);
  ellipse(ctx, ex + 1.5, ey, 2.6, 2.8);
  ctx.fillStyle = INK;
  ctx.fill();
  ctx.fillStyle = "#fff";
  ctx.fillRect(ex + 2, ey - 2, 1.4, 1.4);
  if (L.eyes === "sleepy") {
    ctx.beginPath();
    ctx.arc(ex, ey, 5.5, Math.PI, 0);
    ctx.closePath();
    paint(ctx, L.head ?? L.body, 2);
  } else if (L.eyes === "fierce") {
    ctx.lineWidth = 4;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(ex - 6, ey - 9);
    ctx.lineTo(ex + 7, ey - 4);
    ctx.stroke();
    ctx.lineCap = "butt";
  }
}

function drawExtra(ctx: Ctx, L: BirdLook, hx: number, hy: number, hr: number, cx: number, cy: number, time: number): void {
  const ex = hx + hr * 0.3;
  const ey = hy - hr * 0.22;
  const col = L.extraColor ?? "#d32f2f";
  ctx.strokeStyle = INK;
  switch (L.extra) {
    case "eyepatch":
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(hx - hr, hy - hr * 0.7);
      ctx.lineTo(hx + hr * 0.8, hy + hr * 0.1);
      ctx.stroke();
      ellipse(ctx, ex, ey + 1, 6, 6.5);
      paint(ctx, "#151515", 2);
      break;
    case "scar":
      ctx.strokeStyle = "#7a2418";
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(ex - 4, ey - 10);
      ctx.lineTo(ex + 5, ey + 9);
      ctx.moveTo(ex - 3, ey - 4);
      ctx.lineTo(ex + 2, ey - 6);
      ctx.moveTo(ex, ey + 3);
      ctx.lineTo(ex + 5, ey + 1);
      ctx.stroke();
      break;
    case "bandana": {
      ctx.beginPath();
      ctx.moveTo(hx - hr - 1, hy - hr * 0.25);
      ctx.quadraticCurveTo(hx, hy - hr * 0.75, hx + hr + 1, hy - hr * 0.55);
      ctx.lineTo(hx + hr, hy - hr * 0.15);
      ctx.quadraticCurveTo(hx, hy - hr * 0.35, hx - hr - 1, hy + hr * 0.15);
      ctx.closePath();
      paint(ctx, col, 2);
      const wave = Math.sin(time * 8) * 3;
      for (const k of [0, 1]) {
        ctx.beginPath();
        ctx.moveTo(hx - hr, hy - hr * 0.05);
        ctx.quadraticCurveTo(hx - hr - 10, hy - 2 + k * 6 + wave, hx - hr - 20, hy + 4 + k * 8 - wave);
        ctx.lineTo(hx - hr - 16, hy + 10 + k * 8 - wave);
        ctx.quadraticCurveTo(hx - hr - 8, hy + 6 + k * 4, hx - hr + 1, hy + 3);
        ctx.closePath();
        paint(ctx, col, 2);
      }
      break;
    }
    case "monocle":
      ellipse(ctx, ex + 1, ey, 6.5, 6.5);
      ctx.lineWidth = 2.5;
      ctx.strokeStyle = "#d4a017";
      ctx.stroke();
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(ex + 1, ey + 6.5);
      ctx.quadraticCurveTo(ex - 6, ey + 22, ex - 12, ey + 26);
      ctx.stroke();
      break;
    case "mustache":
      ctx.beginPath();
      ctx.moveTo(hx + hr * 0.8, hy + 5);
      ctx.bezierCurveTo(hx + hr + 12, hy + 1, hx + hr + 18, hy + 14, hx + hr + 22, hy + 4);
      ctx.bezierCurveTo(hx + hr + 16, hy + 20, hx + hr + 4, hy + 12, hx + hr * 0.8, hy + 8);
      ctx.closePath();
      paint(ctx, "#2a1a10", 1.5);
      break;
    case "bowtie": {
      const bx = (hx + cx) / 2 + 12;
      const by = (hy + cy) / 2 + 10;
      ctx.beginPath();
      ctx.moveTo(bx, by);
      ctx.lineTo(bx - 9, by - 7);
      ctx.lineTo(bx - 9, by + 7);
      ctx.closePath();
      ctx.moveTo(bx, by);
      ctx.lineTo(bx + 9, by - 7);
      ctx.lineTo(bx + 9, by + 7);
      ctx.closePath();
      paint(ctx, col, 2);
      ellipse(ctx, bx, by, 3, 3);
      paint(ctx, col, 2);
      break;
    }
    case "none":
      break;
  }
}

// ---------------------------------------------------------------- humans

function drawHuman(ctx: Ctx, L: HumanLook, g: GearView, p: Pose, time: number): void {
  ctx.save();
  ctx.scale(L.size, L.size);
  const legLen = 72 * (1 - p.sit * 0.35);
  const hipY = -legLen;
  const top = hipY - 74;
  const jacket = L.jacket ?? L.shirt;
  const sleeve = L.jacket ?? L.shirt;

  // legs
  for (const near of [false, true]) {
    const phase = p.walk + (near ? Math.PI : 0);
    const fx = (near ? 6 : -4) + Math.sin(phase) * 14 * p.stride;
    const lift = Math.max(0, Math.cos(phase)) * 8 * p.stride;
    ctx.lineCap = "round";
    ctx.strokeStyle = INK;
    ctx.lineWidth = 19;
    ctx.beginPath();
    ctx.moveTo(near ? 4 : -4, hipY);
    ctx.lineTo(fx, -10 - lift);
    ctx.stroke();
    ctx.strokeStyle = near ? L.pants : shade(L.pants, -0.15);
    ctx.lineWidth = 14;
    ctx.stroke();
    ctx.lineCap = "butt";
    ctx.beginPath();
    ctx.roundRect(fx - 9, -12 - lift, 26, 12, 6);
    paint(ctx, L.hat === "cowboy" ? "#5a3418" : "#2a2420", 2.5);
  }

  ctx.save();
  ctx.translate(0, hipY);
  ctx.rotate(p.lean * 0.7);
  ctx.translate(0, -hipY);

  // far arm (shield arm)
  const shoulderY = top + 10;
  ctx.save();
  ctx.translate(4, shoulderY);
  ctx.rotate(1.1 - p.guard * 1.0 - p.flap * 1.6);
  drawArm(ctx, shade(sleeve, -0.18), L.skin);
  ctx.restore();

  // torso
  ctx.beginPath();
  ctx.moveTo(-18, top + 6);
  ctx.quadraticCurveTo(-20, hipY - 10, -16, hipY + 4);
  ctx.lineTo(18, hipY + 4);
  ctx.quadraticCurveTo(28, hipY - 26, 20, top + 6);
  ctx.quadraticCurveTo(0, top - 4, -18, top + 6);
  ctx.closePath();
  ctx.fillStyle = L.overalls ? L.shirt : jacket;
  ctx.fill();
  ctx.save();
  ctx.clip();
  if (L.jacket) {
    ctx.beginPath();
    ctx.moveTo(6, top);
    ctx.lineTo(28, top);
    ctx.lineTo(28, hipY - 30);
    ctx.closePath();
    ctx.fillStyle = L.shirt;
    ctx.fill();
    if (L.tie) {
      ctx.beginPath();
      ctx.moveTo(19, top + 2);
      ctx.lineTo(24, top + 2);
      ctx.lineTo(23, hipY - 34);
      ctx.lineTo(19, hipY - 38);
      ctx.closePath();
      paint(ctx, L.tie, 1.5);
    }
  }
  if (L.overalls) {
    ctx.fillStyle = L.pants;
    ctx.fillRect(-20, hipY - 34, 50, 40);
    ctx.fillRect(-2, hipY - 52, 30, 22);
    ctx.strokeStyle = shade(L.pants, -0.3);
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(2, hipY - 50);
    ctx.lineTo(-10, top + 2);
    ctx.stroke();
    ctx.fillStyle = "#e8c66a";
    ctx.fillRect(0, hipY - 50, 4, 4);
  }
  if (L.sash) {
    ctx.strokeStyle = L.sash;
    ctx.lineWidth = 9;
    ctx.beginPath();
    ctx.moveTo(-18, top + 4);
    ctx.lineTo(26, hipY - 6);
    ctx.stroke();
  }
  ctx.restore();
  ctx.lineWidth = 3;
  ctx.strokeStyle = INK;
  ctx.stroke();
  ctx.fillStyle = "#2a2420";
  ctx.fillRect(-17, hipY - 4, 36, 7);
  if (L.badge) {
    starPath(ctx, 16, top + 26, 7, 3);
    paint(ctx, "#f5c431", 2);
  }

  // head
  const hx = 6;
  const hy = top - 16;
  ellipse(ctx, hx - 2, top + 2, 8, 6);
  paint(ctx, L.skin, 2);
  ellipse(ctx, hx, hy, 19, 21);
  paint(ctx, L.skin);
  ctx.beginPath();
  ctx.moveTo(hx + 17, hy - 4);
  ctx.quadraticCurveTo(hx + 27, hy + 4, hx + 18, hy + 6);
  paint(ctx, L.skin, 2.5);
  ellipse(ctx, hx - 6, hy + 2, 4, 6);
  paint(ctx, shade(L.skin, -0.08), 2);
  // hair
  ctx.beginPath();
  ctx.moveTo(hx - 19, hy + 4);
  ctx.quadraticCurveTo(hx - 22, hy - 24, hx + 2, hy - 22);
  ctx.quadraticCurveTo(hx + 18, hy - 20, hx + 18, hy - 10);
  ctx.quadraticCurveTo(hx, hy - 14, hx - 8, hy - 6);
  ctx.quadraticCurveTo(hx - 12, hy + 2, hx - 19, hy + 4);
  ctx.closePath();
  paint(ctx, L.hair, 2.5);
  // eye
  const ex = hx + 9;
  const ey = hy - 3;
  ctx.strokeStyle = INK;
  ctx.lineWidth = 2.5;
  if (p.eyes === "x") {
    ctx.beginPath();
    ctx.moveTo(ex - 3, ey - 3);
    ctx.lineTo(ex + 3, ey + 3);
    ctx.moveTo(ex + 3, ey - 3);
    ctx.lineTo(ex - 3, ey + 3);
    ctx.stroke();
  } else if (p.eyes === "closed") {
    ctx.beginPath();
    ctx.moveTo(ex - 3, ey);
    ctx.lineTo(ex + 3, ey);
    ctx.stroke();
  } else {
    ellipse(ctx, ex, ey, 2.6, 3.2);
    ctx.fillStyle = INK;
    ctx.fill();
  }
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(ex - 4, ey - 8);
  ctx.lineTo(ex + 5, ey - 6);
  ctx.stroke();
  if (L.glasses) {
    ellipse(ctx, ex + 1, ey, 6, 5.5);
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(ex - 5, ey - 1);
    ctx.lineTo(hx - 4, hy - 2);
    ctx.stroke();
  }
  // mouth / beard
  if (L.beard === "goatee") {
    ctx.beginPath();
    ctx.moveTo(hx + 8, hy + 10);
    ctx.quadraticCurveTo(hx + 20, hy + 8, hx + 18, hy + 14);
    ctx.quadraticCurveTo(hx + 16, hy + 28, hx + 8, hy + 22);
    ctx.closePath();
    paint(ctx, L.hair, 2);
  } else if (L.beard === "stubble") {
    ctx.fillStyle = "rgba(60,40,30,0.35)";
    ellipse(ctx, hx + 6, hy + 12, 13, 8);
    ctx.fill();
  }
  if (L.beard === "mustache" || L.beard === "goatee") {
    ctx.beginPath();
    ctx.moveTo(hx + 10, hy + 9);
    ctx.quadraticCurveTo(hx + 18, hy + 4, hx + 24, hy + 10);
    ctx.quadraticCurveTo(hx + 18, hy + 9, hx + 10, hy + 12);
    ctx.closePath();
    paint(ctx, L.beard === "goatee" ? L.hair : "#3a2a1e", 1.5);
  }
  ctx.beginPath();
  if (p.mouth > 0.1) {
    ellipse(ctx, hx + 16, hy + 14, 3.5, 2 + p.mouth * 4);
    paint(ctx, "#5a1a1a", 2);
  } else {
    ctx.moveTo(hx + 12, hy + 14);
    ctx.lineTo(hx + 18, hy + 13);
    ctx.lineWidth = 2;
    ctx.strokeStyle = INK;
    ctx.stroke();
  }
  // hat or helmet
  if (g.helmet) {
    ctx.save();
    ctx.translate(hx, hy);
    drawHelmet(ctx, g.helmet, 20, time);
    ctx.restore();
  } else drawHat(ctx, L.hat, hx, hy);

  // shield
  if (g.shield) {
    ctx.save();
    ctx.translate(32 + p.guard * 14, top + 44 - p.guard * 16);
    ctx.scale(1.25, 1.25);
    drawShield(ctx, g.shield, time);
    ctx.restore();
  }

  // weapon arm
  ctx.save();
  ctx.translate(2, shoulderY);
  ctx.rotate(p.wing + 0.35 - (g.shield ? 0 : p.flap * 1.6));
  if (g.weapon) {
    ctx.save();
    ctx.translate(46, 0);
    ctx.rotate(weaponOffset(g.weapon));
    ctx.scale(1.3, 1.3);
    drawWeapon(ctx, g.weapon, time);
    ctx.restore();
  }
  drawArm(ctx, sleeve, L.skin);
  ctx.restore();

  ctx.restore();
  ctx.restore();
}

function drawArm(ctx: Ctx, sleeve: string, skin: string): void {
  ctx.lineCap = "round";
  ctx.strokeStyle = INK;
  ctx.lineWidth = 17;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(40, 0);
  ctx.stroke();
  ctx.strokeStyle = sleeve;
  ctx.lineWidth = 12;
  ctx.stroke();
  ctx.lineCap = "butt";
  ellipse(ctx, 45, 0, 7, 7);
  paint(ctx, skin, 2.5);
}

function drawHat(ctx: Ctx, hat: HumanLook["hat"], hx: number, hy: number): void {
  switch (hat) {
    case "straw":
      ellipse(ctx, hx, hy - 16, 36, 7);
      paint(ctx, "#e8c66a");
      ctx.beginPath();
      ctx.moveTo(hx - 16, hy - 17);
      ctx.quadraticCurveTo(hx - 16, hy - 40, hx, hy - 40);
      ctx.quadraticCurveTo(hx + 16, hy - 40, hx + 16, hy - 17);
      ctx.closePath();
      paint(ctx, "#e8c66a");
      ctx.fillStyle = "#b3261e";
      ctx.fillRect(hx - 16, hy - 24, 32, 5);
      break;
    case "cowboy":
      ctx.beginPath();
      ctx.moveTo(hx - 38, hy - 22);
      ctx.quadraticCurveTo(hx, hy - 6, hx + 38, hy - 22);
      ctx.quadraticCurveTo(hx, hy - 12, hx - 38, hy - 22);
      paint(ctx, "#7a4a22");
      ctx.beginPath();
      ctx.moveTo(hx - 17, hy - 16);
      ctx.quadraticCurveTo(hx - 20, hy - 46, hx - 6, hy - 42);
      ctx.lineTo(hx, hy - 36);
      ctx.lineTo(hx + 6, hy - 42);
      ctx.quadraticCurveTo(hx + 20, hy - 46, hx + 17, hy - 16);
      ctx.closePath();
      paint(ctx, "#7a4a22");
      ctx.fillStyle = "#3a2210";
      ctx.fillRect(hx - 17, hy - 22, 34, 5);
      break;
    case "top":
      ellipse(ctx, hx, hy - 17, 28, 6);
      paint(ctx, "#151518");
      ctx.beginPath();
      ctx.roundRect(hx - 16, hy - 62, 32, 46, 3);
      paint(ctx, "#151518");
      ctx.fillStyle = "#7a1f3d";
      ctx.fillRect(hx - 16, hy - 26, 32, 6);
      break;
    case "chef":
      ctx.beginPath();
      ctx.roundRect(hx - 16, hy - 30, 32, 16, 3);
      paint(ctx, "#fff");
      ellipse(ctx, hx, hy - 38, 20, 14);
      paint(ctx, "#fff");
      break;
    case "none":
      break;
  }
}

export function starPath(ctx: Ctx, x: number, y: number, r: number, ri: number, points = 5): void {
  ctx.beginPath();
  for (let i = 0; i < points * 2; i++) {
    const rr = i % 2 ? ri : r;
    const a = (i / (points * 2)) * Math.PI * 2 - Math.PI / 2;
    ctx.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr);
  }
  ctx.closePath();
}
